import { useCallback, useMemo } from "react";
import { getOddByCompanyId, filterUniqueScoresByCompany } from "@/utils/matchInfo";

const getOddWithHalfTimeByCompanyId = (useFirstResult, odds, halfOdds, companyIdOdds) => {
  if (!odds) return null;

  const baseOdd = useFirstResult 
    ? odds[0] 
    : odds.find((item) => item.companyId == companyIdOdds);

  if (!baseOdd) return null;
  
  return {
    ...baseOdd,
    halfTime: halfOdds?.find((halfOdd) => halfOdd.companyId === baseOdd.companyId) || null,
  };
};

export const useFilterOdds = (
  match,
  liveOdds,
  liveOtherOdds,
  companyIdOdds = -1,
  otherOdds = {}
) => {
  const filterOdds = useCallback(
    (type, liveData, matchData) => {
      let liveOdd = null;
      let matchOdd = null;
      const allBookmarker = Number(companyIdOdds) === -1;
      const useFirstResult = allBookmarker;

      switch (type) {
        case "europe":
          liveOdd = getOddByCompanyId(useFirstResult, liveData?.europeOdds, companyIdOdds);
          matchOdd = getOddByCompanyId(useFirstResult, matchData?.europeOdds, companyIdOdds);
          break;

        case "handicap":
          liveOdd = getOddByCompanyId(useFirstResult, liveData?.handicap, companyIdOdds);
          matchOdd = getOddWithHalfTimeByCompanyId(
            useFirstResult,
            matchData?.handicapOdds,
            matchData?.handicapHalfOdds,
            companyIdOdds
          );
          break;

        case "overUnder":
          liveOdd = getOddByCompanyId(useFirstResult, liveData?.overUnder, companyIdOdds);
          matchOdd = getOddWithHalfTimeByCompanyId(
            useFirstResult,
            matchData?.overUnderOdds,
            matchData?.overUnderHalfOdds,
            companyIdOdds
          );
          break;

        case "correctScores":
          matchOdd = getOddByCompanyId(useFirstResult, matchData?.correctScoresOdds, companyIdOdds);
          break;

        case "correctScoresHalf":
          matchOdd = getOddByCompanyId(useFirstResult, matchData?.correctScoresHalfOdds, companyIdOdds);
          break;

        case "totalCorners":
          matchOdd = getOddByCompanyId(useFirstResult, matchData?.totalCornersOdds, companyIdOdds);
          break;

        case "totalCornersHalf":
          matchOdd = getOddByCompanyId(useFirstResult, matchData?.totalCornersHalfOdds, companyIdOdds);
          break;

        case "handicapHalf":
          liveOdd = getOddByCompanyId(useFirstResult, liveData?.handicapHalf, companyIdOdds);
          matchOdd = getOddByCompanyId(useFirstResult, matchData?.handicapHalfOdds, companyIdOdds);
          break;

        case "overUnderHalf":
          liveOdd = getOddByCompanyId(useFirstResult, liveData?.overUnderHalf, companyIdOdds);
          matchOdd = getOddByCompanyId(useFirstResult, matchData?.overUnderHalfOdds, companyIdOdds);
          break;

        case "europeHalf":
          liveOdd = getOddByCompanyId(useFirstResult, liveData?.europeHalf, companyIdOdds);
          matchOdd = getOddByCompanyId(useFirstResult, matchData?.europeHalfOdds, companyIdOdds);
          break;
      }

      return liveOdd || matchOdd;
    },
    [companyIdOdds]
  );

  const filterOtherOdds = useCallback(
    (type, dataLiveOdds, preMatchOdds) => {
      const liveOdds = dataLiveOdds?.[type];
      const preOdds = preMatchOdds?.[`${type}Odds`];
      const allBookmarker = Number(companyIdOdds) === -1;
      const useFirstResult = allBookmarker;

      const tmp = {
        live: liveOdds?.filter((item) => item.companyId == companyIdOdds),
        pre: !useFirstResult
          ? preOdds?.filter((item) => item.companyId == companyIdOdds)
          : preOdds,
      };
      if (Array.isArray(tmp.pre) && tmp.pre.length > 0) {
        tmp.pre = filterUniqueScoresByCompany(tmp.pre);
      }
      if (Array.isArray(tmp.live) && tmp.live.length > 0) {
        tmp.live = filterUniqueScoresByCompany(tmp.live);
      }
      return tmp;
    },
    [companyIdOdds]
  );

  const filterHandicapOdds = useMemo(() => {
    return filterOdds("handicap", liveOdds, match);
  }, [liveOdds, match, filterOdds]);

  const filterOverUnderOdds = useMemo(() => {
    return filterOdds("overUnder", liveOdds, match);
  }, [liveOdds, match, filterOdds]);

  const filterEuropeOdds = useMemo(() => {
    return filterOdds("europe", liveOdds, match);
  }, [liveOdds, match, filterOdds]);

  const filterHandicapHalfOdds = useMemo(() => {
    return filterOdds("handicapHalf", liveOdds, match);
  }, [liveOdds, match, filterOdds]);

  const filterOverUnderHalfOdds = useMemo(() => {
    return filterOdds("overUnderHalf", liveOdds, match);
  }, [liveOdds, match, filterOdds]);

  const filterEuropeHalfOdds = useMemo(() => {
    return filterOdds("europeHalf", liveOdds, match);
  }, [liveOdds, match, filterOdds]);

  const filterOtherOddsData = useMemo(() => {
    const _dataMatch = { ...match, ...otherOdds };
    return {
      goalOddEvenOdds: filterOtherOdds("goalsOddEven", liveOtherOdds, _dataMatch),
      correctScoresOdds: filterOtherOdds("correctScores", liveOtherOdds, _dataMatch),
      correctScoresHalfOdds: filterOtherOdds("correctScoresHalf", liveOtherOdds, _dataMatch),
      totalCornersOdds: filterOtherOdds("totalCorners", liveOtherOdds, _dataMatch),
      totalCornersHalfOdds: filterOtherOdds("totalCornersHalf", liveOtherOdds, _dataMatch),
    };
  }, [liveOtherOdds, match, otherOdds, filterOtherOdds]);

  return {
    filterHandicapOdds,
    filterOverUnderOdds,
    filterEuropeOdds,
    filterHandicapHalfOdds,
    filterOverUnderHalfOdds,
    filterEuropeHalfOdds,
    filterOtherOddsData,
  };
};
